import { useMemo, useState } from "react";
import {
  Download,
  Eye,
  FileText,
  Globe2,
  Lock,
  Plus,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import {
  useArtifactContent,
  useArtifacts,
  useArtifactValidations,
  useCertificateRequests,
  useCreateInlineArtifact,
  useDeleteArtifact,
  useDownloadArtifactFile,
  useRepresentatives,
  useSetArtifactVisibility,
  useUploadArtifact,
} from "@/hooks/queries";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Dialog } from "@/components/ui/Dialog";
import { Field, Input, Select, Textarea } from "@/components/ui/Input";
import { EmptyState, TBody, TD, TH, THead, TR, Table } from "@/components/ui/Table";
import { CenteredSpinner } from "@/components/ui/Spinner";
import { ErrorState } from "@/components/ui/ErrorState";
import { Badge } from "@/components/ui/Badge";
import { SecretField } from "@/components/ui/SecretField";
import { formatDate, readFileAsBase64, readFileAsText, saveBlob } from "@/lib/utils";
import {
  ENCRYPTED_ARTIFACT_TYPES,
  FILE_ARTIFACT_TYPES,
  INLINE_ARTIFACT_TYPES,
  isR2Backed,
  type Artifact,
  type ArtifactValidation,
  type ArtifactType,
  type CreateInlineArtifactInput,
  type FileArtifactType,
  type InlineArtifactType,
  type UploadArtifactInput,
} from "@/types";

const ARTIFACT_TYPE_LABELS: Record<ArtifactType, string> = {
  rut: "RUT",
  camara_comercio: "Chamber of commerce",
  cedula: "ID document",
  poder: "Power of attorney",
  formulario: "Form",
  attachment: "Attachment",
  response_document: "Response document",
  csr: "CSR",
  private_key: "Private key",
  pem_certificate: "PEM certificate",
  pfx: "PFX",
  pfx_password: "PFX password",
  p12: "P12",
  p12_password: "P12 password",
};

function artifactTypeLabel(type: ArtifactType | string) {
  return ARTIFACT_TYPE_LABELS[type as ArtifactType] ?? type.replace(/_/g, " ");
}

// AI document-validation verdict (PC-29). Hover shows the flagged reasons.
function ValidationBadge({ v }: { v: ArtifactValidation }) {
  const issues = v.checks.filter((c) => c.status !== "pass");
  const tone = v.overall === "pass" ? "success" : v.overall === "fail" ? "danger" : "warning";
  const Icon = v.overall === "pass" ? ShieldCheck : ShieldAlert;
  const label =
    v.overall === "pass" ? "AI OK" : v.overall === "fail" ? "AI: review" : "AI: check";
  const tooltip = issues.length
    ? issues.map((c) => `• ${c.detail ?? c.label}`).join("\n")
    : "Document verified by AI";
  return (
    <span title={tooltip} className="inline-flex">
      <Badge tone={tone} className="gap-1">
        <Icon className="h-3 w-3" /> {label}
      </Badge>
    </span>
  );
}

export function ArtifactsTab({ subscriberId }: { subscriberId: string }) {
  const list = useArtifacts(subscriberId);
  const reps = useRepresentatives(subscriberId);
  const reqs = useCertificateRequests(subscriberId);
  const uploadFile = useUploadArtifact(subscriberId);
  const createInline = useCreateInlineArtifact(subscriberId);
  const remove = useDeleteArtifact(subscriberId);
  const downloadMutation = useDownloadArtifactFile(subscriberId);
  const setVisibility = useSetArtifactVisibility(subscriberId);
  const validations = useArtifactValidations(subscriberId);

  const validationByArtifact = useMemo(() => {
    const m = new Map<string, ArtifactValidation>();
    for (const v of validations.data ?? []) m.set(v.artifactId, v);
    return m;
  }, [validations.data]);

  const [addMode, setAddMode] = useState<"none" | "file" | "inline">("none");
  const [viewId, setViewId] = useState<string | null>(null);

  async function onDelete(a: Artifact) {
    if (!confirm(`Delete artifact "${artifactTypeLabel(a.type)}"?`)) return;
    try {
      await remove.mutateAsync(a.id);
      toast.success("Artifact deleted");
    } catch (err) {
      if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
      else toast.error("Failed to delete");
    }
  }

  async function onDownload(a: Artifact) {
    try {
      const { blob, fileName } = await downloadMutation.mutateAsync(a.id);
      saveBlob(blob, fileName);
      toast.success(`Downloaded ${fileName}`);
    } catch (err) {
      if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
      else toast.error("Failed to download");
    }
  }

  async function onToggleVisibility(a: Artifact) {
    const visibility = a.visibility === "public" ? "internal" : "public";
    try {
      await setVisibility.mutateAsync({ artifactId: a.id, visibility });
      toast.success(
        visibility === "public"
          ? "Artifact is visible to the customer"
          : "Artifact is internal again"
      );
    } catch (err) {
      if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
      else toast.error("Failed to update visibility");
    }
  }

  const repOptions = reps.data ?? [];
  const reqOptions = reqs.data ?? [];

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex items-center justify-between">
        <CardTitle>Artifacts</CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setAddMode("file")}>
            <Upload className="h-3.5 w-3.5" /> Upload document
          </Button>
          <Button size="sm" onClick={() => setAddMode("inline")}>
            <Plus className="h-3.5 w-3.5" /> Inline / crypto
          </Button>
        </div>
      </CardHeader>
      {list.isPending ? (
        <CenteredSpinner />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : list.data.length === 0 ? (
        <EmptyState
          title="No artifacts"
          description="Upload supporting documents (RUT, Cámara de Comercio, etc.) or paste / upload crypto material (CSR, key, PEM, PFX)."
        />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>Type</TH>
              <TH>File / Content</TH>
              <TH>Linked to</TH>
              <TH>Visibility</TH>
              <TH>Created</TH>
              <TH className="text-right">Actions</TH>
            </tr>
          </THead>
          <TBody>
            {list.data.map((a) => {
              const r2 = isR2Backed(a.fileUrl);
              const externalUrl = a.fileUrl && !r2 ? a.fileUrl : null;
              return (
                <TR key={a.id}>
                  <TD>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-900">
                        {artifactTypeLabel(a.type)}
                      </span>
                      {a.encrypted && (
                        <Badge tone="warning">
                          <Lock className="h-3 w-3" /> encrypted
                        </Badge>
                      )}
                      {r2 && <Badge tone="info">R2</Badge>}
                      {validationByArtifact.has(a.id) && (
                        <ValidationBadge v={validationByArtifact.get(a.id)!} />
                      )}
                    </div>
                  </TD>
                  <TD>
                    {r2 ? (
                      <span className="text-xs text-slate-900 inline-flex items-center gap-1">
                        <FileText className="h-3.5 w-3.5" />
                        {a.fileName ?? "(unnamed)"}
                      </span>
                    ) : externalUrl ? (
                      <a
                        href={externalUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-brand-600 hover:underline inline-flex items-center gap-1 text-xs"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        {a.fileName ?? externalUrl}
                      </a>
                    ) : (
                      <span className="text-xs text-slate-500">inline</span>
                    )}
                  </TD>
                  <TD className="text-xs">
                    {a.representativeId && (
                      <p>
                        rep:{" "}
                        {repOptions.find((r) => r.id === a.representativeId)?.fullName ??
                          a.representativeId.slice(0, 8)}
                      </p>
                    )}
                    {a.certificateRequestId && <p>req: {a.certificateRequestId.slice(0, 8)}…</p>}
                    {!a.representativeId && !a.certificateRequestId && (
                      <span className="text-slate-400">subscriber</span>
                    )}
                  </TD>
                  <TD>
                    {a.visibility === "public" ? (
                      <Badge tone="success" className="gap-1">
                        <Globe2 className="h-3 w-3" /> public
                      </Badge>
                    ) : (
                      <Badge tone="neutral" className="gap-1">
                        <Lock className="h-3 w-3" /> internal
                      </Badge>
                    )}
                  </TD>
                  <TD className="text-[11px] text-slate-500">{formatDate(a.createdAt)}</TD>
                  <TD className="text-right">
                    <div className="inline-flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onToggleVisibility(a)}
                        loading={
                          setVisibility.isPending &&
                          setVisibility.variables?.artifactId === a.id
                        }
                        aria-label={a.visibility === "public" ? "Make internal" : "Make public"}
                        title={a.visibility === "public" ? "Make internal" : "Make public"}
                      >
                        {a.visibility === "public" ? (
                          <Lock className="h-3.5 w-3.5" />
                        ) : (
                          <Globe2 className="h-3.5 w-3.5" />
                        )}
                      </Button>
                      {r2 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onDownload(a)}
                          loading={
                            downloadMutation.isPending && downloadMutation.variables === a.id
                          }
                          aria-label="Download"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {!r2 && (
                        <Button size="sm" variant="ghost" onClick={() => setViewId(a.id)}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => onDelete(a)}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      )}

      {addMode === "file" && (
        <UploadDocumentDialog
          onClose={() => setAddMode("none")}
          onSubmit={async (input) => {
            try {
              await uploadFile.mutateAsync(input);
              toast.success("Document uploaded");
              setAddMode("none");
            } catch (err) {
              if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
              else toast.error("Failed to upload");
            }
          }}
          pending={uploadFile.isPending}
          representatives={repOptions.map((r) => ({ id: r.id, label: r.fullName }))}
          certificateRequests={reqOptions.map((r) => ({
            id: r.id,
            label: `${r.type} · ${r.status}`,
          }))}
        />
      )}

      {addMode === "inline" && (
        <AddInlineDialog
          onClose={() => setAddMode("none")}
          onSubmit={async (input) => {
            try {
              await createInline.mutateAsync(input);
              toast.success("Artifact stored");
              setAddMode("none");
            } catch (err) {
              if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
              else toast.error("Failed to store artifact");
            }
          }}
          pending={createInline.isPending}
          representatives={repOptions.map((r) => ({ id: r.id, label: r.fullName }))}
          certificateRequests={reqOptions.map((r) => ({
            id: r.id,
            label: `${r.type} · ${r.status}`,
          }))}
        />
      )}

      <ViewDialog
        subscriberId={subscriberId}
        artifactId={viewId}
        onClose={() => setViewId(null)}
      />
    </Card>
  );
}

// ── Upload document (R2) ────────────────────────────────────────────────────

function UploadDocumentDialog({
  onClose,
  onSubmit,
  pending,
  representatives,
  certificateRequests,
}: {
  onClose: () => void;
  onSubmit: (input: UploadArtifactInput) => Promise<void>;
  pending: boolean;
  representatives: { id: string; label: string }[];
  certificateRequests: { id: string; label: string }[];
}) {
  const [type, setType] = useState<FileArtifactType>("rut");
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState("");
  const [visibility, setVisibility] = useState<"internal" | "public">("internal");
  const [representativeId, setRepresentativeId] = useState("");
  const [certificateRequestId, setCertificateRequestId] = useState("");

  const tooLarge = !!file && file.size > 10 * 1024 * 1024;

  return (
    <Dialog
      open
      onClose={onClose}
      title="Upload supporting document"
      description="Binary files are stored in R2 under the subscriber folder. Max 10 MB."
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            form="artifact-upload-form"
            type="submit"
            loading={pending}
            disabled={!file || tooLarge}
          >
            Upload
          </Button>
        </>
      }
    >
      <form
        id="artifact-upload-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!file) return;
          onSubmit({
            type,
            file,
            fileName: fileName.trim() || undefined,
            visibility,
            representativeId: representativeId || undefined,
            certificateRequestId: certificateRequestId || undefined,
          });
        }}
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        <Field label="Type" required>
          <Select value={type} onChange={(e) => setType(e.target.value as FileArtifactType)}>
            {FILE_ARTIFACT_TYPES.map((t) => (
              <option key={t} value={t}>
                {artifactTypeLabel(t)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="File" required hint="PDF, image, or any binary. Max 10 MB.">
          <input
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.tif,.tiff,application/pdf,image/*"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setFile(f);
              if (f && !fileName) setFileName(f.name);
            }}
            className="block w-full text-xs text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
          />
          {file && (
            <p className="text-[11px] text-slate-500 mt-1">
              {file.name} · {(file.size / 1024).toFixed(1)} KB · {file.type || "unknown"}
            </p>
          )}
          {tooLarge && (
            <p className="text-[11px] text-red-600 mt-1">File exceeds 10 MB limit.</p>
          )}
        </Field>
        <Field label="Display name" className="md:col-span-2" hint="Optional — defaults to the filename">
          <Input
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            placeholder={file?.name ?? ""}
            maxLength={256}
          />
        </Field>
        <Field label="Customer visibility" hint="Public artifacts appear in the customer portal when linked to a request.">
          <Select
            value={visibility}
            onChange={(e) => setVisibility(e.target.value as "internal" | "public")}
          >
            <option value="internal">Internal only</option>
            <option value="public">Visible to customer</option>
          </Select>
        </Field>
        <Field label="Link to representative">
          <Select
            value={representativeId}
            onChange={(e) => setRepresentativeId(e.target.value)}
          >
            <option value="">None</option>
            {representatives.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Link to request">
          <Select
            value={certificateRequestId}
            onChange={(e) => setCertificateRequestId(e.target.value)}
          >
            <option value="">None</option>
            {certificateRequests.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </Select>
        </Field>
      </form>
    </Dialog>
  );
}

// ── Inline / crypto ─────────────────────────────────────────────────────────

function AddInlineDialog({
  onClose,
  onSubmit,
  pending,
  representatives,
  certificateRequests,
}: {
  onClose: () => void;
  onSubmit: (input: CreateInlineArtifactInput) => Promise<void>;
  pending: boolean;
  representatives: { id: string; label: string }[];
  certificateRequests: { id: string; label: string }[];
}) {
  const [form, setForm] = useState<CreateInlineArtifactInput>({
    type: "csr",
    content: "",
    visibility: "internal",
  });
  const [reading, setReading] = useState(false);
  const isEncrypted = ENCRYPTED_ARTIFACT_TYPES.includes(form.type);
  const isPfx = form.type === "pfx";

  function detectType(fileName: string): InlineArtifactType | null {
    const n = fileName.toLowerCase();
    if (n.endsWith(".csr")) return "csr";
    if (n.endsWith(".key") || n.endsWith(".pkey")) return "private_key";
    if (n.endsWith(".crt") || n.endsWith(".cer") || n.endsWith(".pem")) return "pem_certificate";
    if (n.endsWith(".pfx") || n.endsWith(".p12")) return "pfx";
    return null;
  }

  async function onFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setReading(true);
    try {
      const detected = detectType(file.name);
      const type = detected ?? form.type;
      let content: string;
      if (type === "pfx") {
        content = await readFileAsBase64(file);
      } else {
        content = await readFileAsText(file);
      }
      setForm((f) => ({ ...f, type, content }));
    } catch {
      toast.error("Could not read file");
    } finally {
      setReading(false);
      e.target.value = "";
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Add inline / crypto artifact"
      description="Paste content or upload a file. Sensitive types are encrypted at rest."
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button form="artifact-inline-form" type="submit" loading={pending}>
            Save
          </Button>
        </>
      }
    >
      <form
        id="artifact-inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({
            ...form,
            visibility: form.visibility ?? "internal",
            representativeId: form.representativeId || undefined,
            certificateRequestId: form.certificateRequestId || undefined,
          });
        }}
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        <Field label="Type" required>
          <Select
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value as InlineArtifactType })}
          >
            {INLINE_ARTIFACT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
                {ENCRYPTED_ARTIFACT_TYPES.includes(t) ? " (encrypted)" : ""}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Read from file" hint="Optional — auto-detects type from extension">
          <input
            type="file"
            accept=".csr,.key,.pkey,.pem,.crt,.cer,.pfx,.p12,.txt"
            onChange={onFilePicked}
            disabled={reading}
            className="block w-full text-xs text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
          />
        </Field>
        <Field label="Customer visibility" hint="Public artifacts appear in the customer portal when linked to a request.">
          <Select
            value={form.visibility ?? "internal"}
            onChange={(e) =>
              setForm({ ...form, visibility: e.target.value as "internal" | "public" })
            }
          >
            <option value="internal">Internal only</option>
            <option value="public">Visible to customer</option>
          </Select>
        </Field>
        <Field
          label="Content"
          required
          className="md:col-span-2"
          hint={
            isPfx
              ? "Binary PFX is stored base64-encoded. Uploading a .pfx fills this for you."
              : isEncrypted
                ? "Will be encrypted with AES-256-GCM at rest."
                : "Plain PEM / text content."
          }
        >
          <Textarea
            rows={10}
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            required
            maxLength={100_000}
          />
        </Field>
        <Field label="Link to representative">
          <Select
            value={form.representativeId ?? ""}
            onChange={(e) =>
              setForm({ ...form, representativeId: e.target.value || undefined })
            }
          >
            <option value="">None</option>
            {representatives.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Link to request">
          <Select
            value={form.certificateRequestId ?? ""}
            onChange={(e) =>
              setForm({ ...form, certificateRequestId: e.target.value || undefined })
            }
          >
            <option value="">None</option>
            {certificateRequests.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </Select>
        </Field>
      </form>
    </Dialog>
  );
}

// ── View inline content ─────────────────────────────────────────────────────

function ViewDialog({
  subscriberId,
  artifactId,
  onClose,
}: {
  subscriberId: string;
  artifactId: string | null;
  onClose: () => void;
}) {
  const q = useArtifactContent(subscriberId, artifactId);
  const open = !!artifactId;
  const encrypted = q.data
    ? ENCRYPTED_ARTIFACT_TYPES.includes(q.data.type as InlineArtifactType)
    : false;
  const filename = q.data
    ? q.data.fileName ??
      (q.data.type === "pfx"
        ? "certificate.pfx.b64"
        : q.data.type === "private_key"
          ? "private-key.pem"
          : q.data.type === "pem_certificate"
            ? "certificate.pem"
            : q.data.type === "csr"
              ? "request.csr"
              : `${q.data.type}.txt`)
    : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Artifact content"
      description={q.data ? artifactTypeLabel(q.data.type) : undefined}
      className="max-w-2xl"
      footer={
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      }
    >
      {q.isPending ? (
        <CenteredSpinner />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : q.data ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            {encrypted && (
              <Badge tone="warning">
                <Lock className="h-3 w-3" /> encrypted at rest
              </Badge>
            )}
            <span className="text-xs text-slate-500 font-mono">{q.data.id}</span>
          </div>
          <SecretField
            label={artifactTypeLabel(q.data.type)}
            value={q.data.content}
            revealDefault={!encrypted}
            multiline
            download={filename ? { filename, mime: "text/plain" } : undefined}
          />
        </div>
      ) : null}
    </Dialog>
  );
}
